import { find, isEmpty, isEqual, isNil } from "lodash";
import i18n from "@plannel/utils/i18n";

import p13nService from "@plannel/services/system/p13n-service";
import debug from "@plannel/utils/debug-util";

const rowSelectedSource = {
  CHECKBOX_SELECTED: "checkboxSelected",
  UI_SELECT_ALL_CURRENT_PAGE: "uiSelectAllCurrentPage",
  ROW_CLICKED: "rowClicked",
  SPACE_KEY: "spaceKey",
}

const DP_WORKBENCH_GRID = "DP_WORKBENCH_GRID";
const IGNORE_P13N_COLLID = "ignoreP13nColId";

/*
 * 언어 리소스는 localStorage(settings.lang) 가 아니라 i18n 인스턴스에서 읽는다.
 * languageChanged 이벤트는 언어 전환 진입점이 localStorage 를 갱신하기 **전에** 발화하므로,
 * 리스너 안에서 localStorage 를 읽으면 이전 언어가 나온다(= 그리드 재번역이 한 박자 밀린다).
 */
const getLanguageBundle = () => i18n.getDataByLanguage(i18n.resolvedLanguage ?? i18n.language) ?? {};

const gridValueL10N = (value, options = {}) => {
  const { grid, translation } = getLanguageBundle();
  const { headerName } = options;

  if (headerName && grid?.[headerName]) {
    return grid[headerName][value];
  }

  if (grid?.[value]) {
    return grid[value];
  }

  return translation?.[value] ?? value;
}

/*
 * measure 표시 이름 — **사용자가 지은 이름은 번역 사전에 넣지 않는다.**
 *
 * 기본 measure 는 name 칸에 번역 키(financeAmt)가 들어 있어 화면에서 풀어야 한다. 그런데 이름을
 * 바꾸면 사용자가 친 글자가 **같은 칸**에 들어오므로, 칸 전체를 gridValueL10N 에 태우면 그 글자가
 * 우연히 번역 키와 겹칠 때 통째로 다른 말로 바뀐다.
 *   실제 신고: 소문자로 치면 대문자가 됐다 — "sum"→SUM, "boh"→BOH, "copy"→COPY, "csv"→CSV …
 *   영어 언어팩에 그런 키가 46개 있다. 대소문자만의 문제가 아니라 "note"→Note 처럼 뜻이 바뀌고,
 *   한국어로 접속하면 "비고"로 보인다 — 사용자가 지은 이름이 언어마다 달라진다.
 *
 * 그래서 **아직 기본값 그대로일 때만** 번역한다. 저장된 값이 기본 키와 정확히 같아야 키로 보고,
 * 한 글자라도 다르면 사용자가 지은 이름이므로 그대로 낸다(기본 키가 없는 사용자 measure 도 마찬가지).
 */
const measureLabel = (name, defaultKey) => (
  defaultKey && name === defaultKey ? gridValueL10N(defaultKey) : (name ?? "")
);

// AdvancedFilter 전용 커스텀 colDef 속성. AG Grid 표준 속성이 아니라
// 그대로 넘기면 "invalid colDef property 'filterType'" 경고가 난다.
// AdvancedFilter 는 (getColumnDefs 를 거치지 않은) 원본 배열에서 읽으므로 여기서 제거해도 안전.
const stripCustomColDefProps = ({ filterType, filterName, ...rest }) => rest;

/*
 * headerName 이 i18n 키인 컬럼은 번역과 함께 그 키를 colDef.context 에 남긴다.
 * 화면이 getColumnDefs 결과를 다시 setColumnDefs 로 넘기면 headerName 은 이미 번역문이라
 * 키를 되찾을 수 없고, 언어 변경 재적용(relocalizeGrid)이 아무 일도 하지 않는 상태가 된다.
 * context 는 AG Grid 표준 속성이라 경고가 없고 cloneDeep 을 거쳐도 남는다.
 */
const I18N_HEADER_KEY = "i18nHeaderKey";

const headerKeyOf = (column) => column?.context?.[I18N_HEADER_KEY] ?? column?.headerName;

/* 번역 대상이 아니면 null. 호출부에서 스프레드하면 원본이 그대로 유지된다. */
const translateHeader = (column, translation) => {
  const headerKey = headerKeyOf(column);

  if (!i18n.exists(headerKey)) {
    return null;
  }

  return {
    headerName: translation?.[headerKey] ?? headerKey,
    context: { ...column.context, [I18N_HEADER_KEY]: headerKey }
  };
};

/*
 * 값이 끼어들거나 여러 용어를 이어 붙여 만드는 헤더는 i18n 키 하나로 표현되지 않아 위 번역을 못 탄다.
 * 렌더 시점의 t 로 만든 문자열은 colDef 에 굳으므로, 조합 함수를 받아 headerName 과 함께
 * headerValueGetter 로도 심는다. 헤더를 그릴 때마다 다시 계산되므로 언어 변경을 따라간다.
 * 조합 함수는 렌더 시점의 t 가 아니라 i18n 싱글턴으로 조합해야 한다.
 */
const composedHeader = (build) => ({ headerName: build(), headerValueGetter: build });

const getColumnDefs = (columns) => {
  const { translation } = getLanguageBundle();

  const columnDefs = [];
  for (const column in columns) {
    if (Object.keys(columns[column]).length > 0) {
      // 그룹 헤더가 번역 대상이 아니어도 children 은 각자 번역한다.
      const translated = translateHeader(columns[column], translation);
      const children = columns[column].children;

      if (children) {
        const childrenColumns = [];
        for (const child in children) {
          if (children[child]) {
            childrenColumns.push(stripCustomColDefProps({
              ...children[child],
              ...translateHeader(children[child], translation)
            }));
          }
        }
        columnDefs.push(stripCustomColDefProps({
          ...columns[column],
          ...translated,
          children: childrenColumns
        }));
      } else {
        columnDefs.push(stripCustomColDefProps({
          ...columns[column],
          ...translated
        }));
      }
    }
  }

  return columnDefs;
}

const autoSizeColumn = (api, skipHeader, columns) => {
  if (api) {
    if (columns && columns.length > 0) {
      api.autoSizeColumns(columns, skipHeader ? skipHeader : false);
    } else {
      api.autoSizeAllColumns(skipHeader ? skipHeader : false);
    }
  }
};

const getColumnIds = (gridApi) => {
  return gridApi.getColumns().map(col => col.getColId());
}

const getAllData = (gridApi) => {
  const data = [];
  const rowCount = gridApi.getDisplayedRowCount();

  for (let i = 0; i < rowCount; i++) {
    data.push(gridApi.getDisplayedRowAtIndex(i).data);
  }

  return data;
}

const addRow = (gridApi, newRow, addIndex = 0, selected = true) => {
  const selectedRows = addIndex > 0 ? [] : gridApi.getSelectedNodes();
  let addRowIndex = addIndex > 0 ? addIndex : 0;

  if (selectedRows && selectedRows.length > 0) {
    addRowIndex = selectedRows[selectedRows.length - 1].rowIndex + 1;
  }

  const createdNode = gridApi.applyTransaction({ add: [newRow], addIndex: addRowIndex }).add[0];

  createdNode.setSelected(selected);
  gridApi.ensureIndexVisible(createdNode.rowIndex);

  return createdNode;
}

const getSortState = (params) => {
  let sortState = {};

  const columnState = params.api.getColumnState();
  const sortColumn = find(columnState, column => isEqual(column.sort, "asc") || isEqual(column.sort, "desc"));
  if (!isEmpty(sortColumn)) {
    sortState = { orderByColumn: sortColumn.colId, sortType: sortColumn.sort };
  }

  return sortState;
}

const getColumnGroupId = (api, columnName) => {
  const defaultGroupId = "";
  for (const column of api.getColumns()) {
    if (isEqual(columnName, column.colId)) {
      return column?.parent?.groupId;
    }
  }
  return defaultGroupId;
}

const focusOnSelectedRow = (gridApi) => {
  const selectedRow = gridApi.getSelectedNodes();
  const index = selectedRow[0] ? selectedRow[0].rowIndex : 0;
  
  gridApi.ensureIndexVisible(index, 'middle');
};

/**
 * If the column contains dynamic elements, the column must be re-defined
 * @param {object} params
 * @param {object} params.api
 * @param {object} params.columnDefs
 * @param {string} [params.viewName]
 * @param {string} [params.gridId='grid']
 * @param {boolean} [params.initState=false] initState=true p13n apply
 * @example 
 * setColumnDefs({ ...params, columnDefs, viewName, initState: true });
 * setColumnDefs({ ...gridRef.current, columnDefs });
*/
// dynamic elements example: values of cellEditorParams, page example: BodItem
/*
 * applyColumnState({ applyOrder: true }) 는 **state 에 없는 컬럼을 맨 뒤로 밀어낸다.**
 * 컬럼이 숨었다 되살아나는 화면(차원 설정으로 컬럼을 껐다 Reset)에서는 되살아난 컬럼이
 * 끝에 붙어버려 columnDefs 순서를 잃는다. state 에 없는 컬럼을 columnDefs 상의 제자리에
 * 끼워 넣은 state 를 돌려준다.
 *
 * 이미 state 에 있는 컬럼끼리의 순서(= 사용자가 끌어 옮긴 순서)는 건드리지 않는다.
 * colId 가 없는 def 는 ag-grid 와 같은 규칙으로 field 를 colId 로 본다.
 */
const mergeColumnStateWithDefs = (columnState, columnDefs) => {
  const defIds = (columnDefs ?? []).map(def => def?.colId ?? def?.field).filter(id => !isNil(id));
  const merged = [...columnState];

  defIds.forEach((defId, defIdx) => {
    if (merged.some(state => isEqual(state.colId, defId))) {
      return;
    }

    // defs 기준 앞쪽 컬럼 중 state 에 이미 있는 가장 가까운 것 바로 뒤에 넣는다(없으면 맨 앞).
    // defs 순서대로 처리하므로 먼저 끼운 컬럼이 다음 컬럼의 기준점이 된다.
    let insertAt = 0;
    for (let i = defIdx - 1; i >= 0; i--) {
      const prevIdx = merged.findIndex(state => isEqual(state.colId, defIds[i]));
      if (prevIdx > -1) {
        insertAt = prevIdx + 1;
        break;
      }
    }
    merged.splice(insertAt, 0, { colId: defId });
  });

  return merged;
};

/*
 * 언어 변경 시 그리드 헤더·셀을 다시 그리기 위한 레지스트리.
 *
 * setColumnDefs 는 **번역된** columnDefs 를 ag-grid 에 밀어 넣는다. 그 상태에서 다시 번역하려 해도
 * headerName 이 이미 번역문이라 i18n.exists() 가 false 가 되어 원래 키를 되찾을 수 없다.
 * 그래서 **번역 전 원본 columnDefs** 를 api 별로 들고 있다가 languageChanged 때 재적용한다.
 *
 * 호출부가 241곳(114파일)이라 화면마다 리스너를 다는 대신 여기 한 곳에서 처리한다.
 * (2026-08-12 추가. 그 전에는 TabContainer 가 언어 변경 때 탭 서브트리를 통째로 remount 하는
 *  버그 덕분에 onGridReady 가 재실행되어 우연히 헤더가 갱신되고 있었다.)
 */
const localizedGrids = new Map();

const registerLocalizedGrid = (api, columnDefs, alignOrderToDefs = false) => {
  if (!api || !columnDefs) {
    return;
  }

  if (!localizedGrids.has(api)) {
    // 그리드가 파기되면 레지스트리에서 뺀다. api 당 1회만 부착한다.
    api.addEventListener?.("gridPreDestroyed", () => localizedGrids.delete(api));
  }

  // 호출부가 배열을 in-place 로 고쳐도 보관본이 흔들리지 않도록 얕은 복제로 들고 있는다.
  localizedGrids.set(api, { columnDefs: [...columnDefs], alignOrderToDefs });
};

/*.
 * rowGroup 의 ag-Grid-AutoColumn 뿐 아니라 체크박스 선택 컬럼(ag-Grid-SelectionColumn),
 * 행번호(ag-Grid-RowNumbersColumn), 계층(ag-Grid-HierarchyColumn) 이 모두 여기 해당한다.
 * 이들은 그리드 옵션에서 다시 생성되므로 스냅샷에 없는 게 정상이다.
 */
const GENERATED_COLUMN_PREFIX = "ag-Grid-";

/* 같은 field 를 쓰는 컬럼이 둘 이상이면 ag-grid 가 뒤쪽에 _1, _2 를 붙여 colId 를 유일하게 만든다.
   스냅샷에는 원래 field 만 있으므로 접미사를 떼고 한 번 더 본다. */
const dedupeSuffixOf = (colId) => colId.replace(/_\d+$/, "");

const collectColIds = (defs, acc = new Set()) => {
  for (const def of defs ?? []) {
    if (!def) {
      continue;
    }
    if (def.children) {
      collectColIds(def.children, acc);
      continue;
    }
    const colId = def.colId ?? def.field;
    if (!isNil(colId)) {
      acc.add(colId);
    }
  }
  return acc;
};

/*
 * 보관한 columnDefs 는 마지막 setColumnDefs 시점의 **스냅샷**이다. 그 뒤 setColumnDefs 를
 * 거치지 않는 경로로 컬럼이 늘었다면, 스냅샷으로 통째 교체할 때 그 컬럼이 사라진다
 * (applyColumnState 는 있는 컬럼의 상태만 되돌릴 뿐 컬럼을 만들어 내지 못한다).
 * 스냅샷이 현재 컬럼을 전부 덮지 못하면 재번역을 건너뛴다 — 번역이 안 되는 편이 낫다.
 * ag-grid 가 스스로 만든 컬럼(GENERATED_COLUMN_PREFIX)과 중복 field 로 붙은 _N 접미사는 제외/보정한다 —
 * 이걸 놓치면 체크박스 그리드나 같은 field 를 두 번 쓰는 화면에서 재번역이 통째로 건너뛰어진다.
 */
const canRelocalize = (api, columnDefs) => {
  const snapshotIds = collectColIds(columnDefs);

  return (api.getColumns() ?? [])
    .map(col => col.getColId())
    .filter(colId => !isNil(colId) && !colId.startsWith(GENERATED_COLUMN_PREFIX))
    .every(colId => snapshotIds.has(colId) || snapshotIds.has(dedupeSuffixOf(colId)));
};

/* p13n 재조회 없이 번역만 다시 입힌다. 사용자가 바꾼 컬럼 폭·순서·표시여부와 필터는 보존한다. */
const relocalizeGrid = (api, { columnDefs, alignOrderToDefs }) => {
  if (!canRelocalize(api, columnDefs)) {
    return;
  }

  const columnState = api.getColumnState();
  const filterModel = api.getFilterModel();

  api.setGridOption("columnDefs", getColumnDefs(columnDefs));

  if (!isNil(columnState)) {
    api.applyColumnState({
      state: alignOrderToDefs ? mergeColumnStateWithDefs(columnState, columnDefs) : columnState,
      applyOrder: true,
    });
  }

  if (!isEmpty(filterModel)) {
    api.setFilterModel(filterModel);
  }

  api.refreshHeader();
  // valueFormatter / cellRenderer 안의 gridValueL10N 을 다시 태워 셀 값까지 새 언어로 바꾼다.
  api.refreshCells({ force: true });
};

const relocalizeAllGrids = () => {
  for (const [api, entry] of [...localizedGrids.entries()]) {
    if (!api || api.isDestroyed?.() === true) {
      localizedGrids.delete(api);
      continue;
    }

    try {
      relocalizeGrid(api, entry);
    } catch (e) {
      // debug() 는 debugMode=false 라 아무것도 남기지 않는다. 한 그리드의 실패가 나머지를
      // 막지는 않되, 원인은 추적할 수 있어야 하므로 여기서는 console.error 로 남긴다.
      console.error("[GridUtils] 언어 변경 재적용 실패", e);
    }
  }
};

i18n.on("languageChanged", relocalizeAllGrids);

const setColumnDefs = async params => {
  const {
    api, columnDefs, viewName, gridId = "grid", initState = false, skipColumnState = false,
    // state 에 없는 컬럼을 맨 뒤가 아니라 columnDefs 제자리에 놓는다. **옵트인** —
    // 호출부가 234곳이라 기본값을 바꾸지 않는다. 컬럼을 껐다 켜는 화면에서만 준다.
    alignOrderToDefs = false,
  } = params;

  const columnState = api?.getColumnState();

  // set column
  if (api && columnDefs) {
    api.setGridOption('columnDefs', getColumnDefs(columnDefs));
    // 언어 변경 때 다시 번역할 수 있도록 **번역 전** 원본을 보관한다.
    registerLocalizedGrid(api, columnDefs, alignOrderToDefs);
  } else {
    debug("Parameter does not have [columnDefs, api]");
  }

  // set column state
  if (initState) {
    if (viewName) {
      const response = await p13nService.get(viewName);
      if (response?.data?.[gridId] && !isEmpty(response.data[gridId])) {
        for (const row of response.data[gridId]) {
          const def = columnDefs.filter(el => el.colId === row.colId)[0];
          if (def) {
            row.sort = def.sort;
          }
        }
        const p13nColumnState = response.data[gridId];
        if (api?.isDestroyed() === false) {
          if (viewName === DP_WORKBENCH_GRID) {

            for (const state of p13nColumnState) {
              for (const col of columnDefs) {
                if (isEqual(col.colId, state.colId) || isEqual(col.field, state.colId)) {
                  if (!isNil(col.rowGroup)) {
                    state.rowGroup = col.rowGroup;
                  }
                  if (!isNil(col.rowGroupIndex)) {
                    state.rowGroupIndex = col.rowGroupIndex;
                  }
                  state.sort = col.sort;
                }
              }
            }

            // p13n 상태에 없는 그룹 컬럼이 있으면 추가해서 rowGroup 순서를 강제 적용한다.
            const stateColIdSet = new Set(p13nColumnState.map((s) => s.colId));
            for (const col of columnDefs) {
              const colId = col.colId ?? col.field;
              if (!colId) {
                continue;
              }
              if (!stateColIdSet.has(colId) && col.rowGroup && !isNil(col.rowGroupIndex)) {
                p13nColumnState.push({
                  colId,
                  rowGroup: true,
                  rowGroupIndex: col.rowGroupIndex,
                  hide: col.hide,
                });
                stateColIdSet.add(colId);
              }
            }

            api.applyColumnState({ state: p13nColumnState });
          } else {
            api.applyColumnState({
              state: alignOrderToDefs ? mergeColumnStateWithDefs(p13nColumnState, columnDefs) : p13nColumnState,
              applyOrder: true,
            });
          }
        } else {
          debug("Grid is destroyed");
        }
      }
    } else {
      debug("Parameter does not have [viewName]");
    }
  } else {
    if (!skipColumnState && !isNil(columnState)) {
      api.applyColumnState({
        state: alignOrderToDefs ? mergeColumnStateWithDefs(columnState, columnDefs) : columnState,
        applyOrder: true,
      });
    }
  }
}

const setRowData = (gridApi, rowData) => {
  gridApi.setGridOption('rowData', rowData);
};

const getVisibleColumns = (gridRef, maxColumnsInGroup = 1) => {
  if (gridRef?.current?.api) {
    const displayedColumns = gridRef.current.api.getAllDisplayedColumns();

    return displayedColumns
      .filter((col) => {
        const parentGroup = col.getParent();
        const usesCustomHeaderGroup = parentGroup?.getColGroupDef()?.headerGroupComponent;

        const isGroupColumn = Boolean(parentGroup) && usesCustomHeaderGroup;
        const isExpanded = isGroupColumn ? parentGroup.isExpanded() : true;

        let isWithinGroupLimit = true;
        if (isGroupColumn) {
          const leafColumns = parentGroup.getLeafColumns();
          const columnIndexInGroup = leafColumns.indexOf(col);
          isWithinGroupLimit = columnIndexInGroup < maxColumnsInGroup;
        }

        return col.isVisible() && (!isGroupColumn || isExpanded || isWithinGroupLimit);
      })
      .map((col) => col.getColDef());
  }
  return [];
};


const GridUtils = {
  autoSizeColumn,
  getColumnIds,
  getColumnDefs,
  composedHeader,
  getAllData,
  addRow,
  getSortState,
  getColumnGroupId,
  focusOnSelectedRow,
  gridValueL10N,
  setColumnDefs,
  registerLocalizedGrid,
  setRowData,
  getVisibleColumns,
  rowSelectedSource,
  IGNORE_P13N_COLLID,
  measureLabel,
}

export default GridUtils;
